// MACHINERY V2 (Stroje) — the katalog model detail below 1280 (M2.16c).
//
// Rendered truth: mobile/screenshots/17-katalog-detail.png. Reference impl:
// mobile/machinery-mobile-katalog.jsx `MbKatalogDetail` — VALUES taken, code
// not. The pieces (back link, collapsible shell, spec row, sticky CTA) are
// catalogDetailMobileParts.tsx; this file is the composition.
//
// Top to bottom, as the board draws it: hero image · mono eyebrow
// (CATEGORY · BRAND) · title · readiness callout · KĽÚČOVÉ PARAMETRE ·
// DOKUMENTÁCIA (n). The back link and the sticky CTA are the page's, because
// they are pinned outside this scrolling column — CatalogDetailV2 owns them.
//
// ============================================================
//  A THIRD SECTION THE BOARD DOES NOT DRAW: ŠPECIFIKÁCIE
// ============================================================
// The board stops at DOKUMENTÁCIA. The desktop detail also renders the FULL
// spec sheet (`SpecSheetView`, with its metric/imperial toggle), and at
// 768-1279 that sheet works TODAY — the katalog detail had no breakpoint branch
// before this sub-phase. Shipping the board literally would therefore DELETE a
// working control from every tablet, which is exactly what
// fleet/fleetSheetSections.tsx:17-19 rules against: the mobile pass never
// removes a working control, it re-homes it. It is re-homed as a third
// collapsible, closed by default, so the board's reading order is untouched for
// anyone who does not want it. Do not "fix" this back to the PNG.
//
// ============================================================
//  THE READINESS CALLOUT IS ALSO THE DISCLOSURE TRIGGER
// ============================================================
// Its dot, its label and its explanatory line all come from
// readiness/readinessTier.ts — the one place the three tiers exist — so this
// panel cannot describe a level differently from the card that led here or the
// legend that explains it. Tapping it opens ReadinessPopupV2, gated on the SAME
// `presentation.interactive` flag the capability badge is gated on. That matters
// here specifically: the desktop aside reaches the popup through
// `CapabilityBadges`, and this layout has none, so without this the popup would
// be unreachable below 1280.
//
// A NULL SPEC FIELD ONCE WHITE-SCREENED /machinery-v2/catalog, and the catalog's
// data is the sparsest in the module. Every optional field below is guarded:
// the hero falls back to the labelled "no photo" panel (`CatalogThumb`), the
// eyebrow drops absent segments rather than printing separators around them,
// and both collapsibles are omitted entirely when they have nothing to list.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useMemo } from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { formatManufacturerWithHierarchy } from "../../../../shared/utils/machineUtils";
import { CatalogModelDetail } from "../../models/models";
import { ResolvedSpecSheet } from "../profile/tabs/specResolve";
import SpecSheetView from "../profile/tabs/SpecSheetView";
import {
  MachineryV2ReadinessPresentation,
  V2_DOC_TYPE_KEYS,
} from "../readiness/readinessPresentation";
import {
  READINESS_TIER_DOT_SIZE,
  readinessTierDescriptor,
} from "../readiness/readinessTier";
import { CatalogThumb, catalogThumbUrl, useCategoryLabel } from "./catalogAtoms";
import { detailAsListItem } from "./catalogDetailAdapter";
import { extractKeySpecs } from "./catalogKeySpecs";
import {
  CatalogDetailSection,
  CatalogDetailSpecRow,
} from "./catalogDetailMobileParts";

export interface CatalogDetailMobileProps {
  model: CatalogModelDetail;
  presentation: MachineryV2ReadinessPresentation;
  sheet: ResolvedSpecSheet;
  phone: boolean;
  onOpenReadiness: () => void;
}

const CatalogDetailMobile: React.FC<CatalogDetailMobileProps> = ({
  model,
  presentation,
  sheet,
  phone,
  onOpenReadiness,
}) => {
  const { translate } = useTranslation("myMachinery");
  const categoryLabel = useCategoryLabel()(model.categoryCode);
  const tier = readinessTierDescriptor(presentation);

  const keySpecs = useMemo(() => extractKeySpecs(model.specSections), [model.specSections]);

  const manufacturerLine = model.manufacturer
    ? formatManufacturerWithHierarchy(
        model.manufacturer.name,
        model.manufacturer.parentManufacturerName
      )
    : "";

  // The board's "FRÉZOVANIE · +GF+ · MIKRON". Absent segments are DROPPED rather
  // than rendered as empty slots, so a model with no category never prints a
  // leading separator.
  const eyebrow = [categoryLabel, manufacturerLine.toUpperCase()]
    .filter((part) => !!part)
    .join(" · ");

  // Only LIT lanes are listed: an unlit lane in a section headed by a count
  // would be a row that contradicts the number above it. The popup remains the
  // place where unlit lanes are shown and explained.
  //
  // >>> AND THIS LIST, NOT `docCount`, IS WHAT GATES THE SECTION (M2.16c
  // review, item 2). <<< The two do not always agree: `totalDocs` falls back to
  // a backend-provided `readiness.totalDocs` when there are no PER-TYPE counts
  // (utils/midasReadiness.ts:133-139), and with no per-type counts every lane
  // comes back unlit — so a total-only readiness headed the section
  // "DOKUMENTACIA 6" and opened onto nothing. Gating on the rows it actually
  // renders makes the header a promise the body keeps. That case is not
  // silently dropped: it is exactly `presentation.docsSummary.kind === "count"`,
  // and the readiness callout above — plus the popup it opens — is where a
  // count with no lanes belongs.
  const litDocRows = presentation.docRows.filter((row) => row.lit);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
      <CatalogThumb
        url={catalogThumbUrl(detailAsListItem(model))}
        alt={model.modelName}
        width="100%"
        height={150}
        radius={12}
      />

      <div style={{ minWidth: 0 }}>
        {eyebrow && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              color: "var(--faint)",
              letterSpacing: "0.1em",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {eyebrow}
          </div>
        )}
        <h1
          style={{
            margin: "3px 0 0",
            fontFamily: "var(--font-sans)",
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: "var(--ink)",
            minWidth: 0,
            wordBreak: "break-word",
          }}
        >
          {model.modelName}
        </h1>
      </div>

      <button
        type="button"
        onClick={() => {
          if (presentation.interactive) onOpenReadiness();
        }}
        aria-haspopup={presentation.interactive ? "dialog" : undefined}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 9,
          width: "100%",
          // >= 44px BOTH axes; the panel is far taller and wider, and the floor
          // is declared so a one-word tier label cannot shrink it.
          minHeight: 44,
          minWidth: 44,
          padding: "9px 12px",
          borderRadius: 10,
          background: "var(--mod-accent-soft)",
          border: "1px solid var(--mod-accent-line)",
          cursor: presentation.interactive ? "pointer" : "default",
          fontFamily: "inherit",
          textAlign: "left",
        }}
      >
        <span
          aria-hidden
          style={{
            width: READINESS_TIER_DOT_SIZE + 1,
            height: READINESS_TIER_DOT_SIZE + 1,
            borderRadius: 999,
            background: tier.ink,
            flexShrink: 0,
            marginTop: 3,
            display: "inline-block",
          }}
        />
        <span style={{ minWidth: 0 }}>
          <span
            style={{
              display: "block",
              fontSize: 12,
              fontWeight: 700,
              color: "var(--ink)",
            }}
          >
            {translate(tier.labelKey)}
          </span>
          <span
            style={{
              display: "block",
              fontSize: 10.5,
              color: "var(--muted)",
              marginTop: 1,
              lineHeight: 1.35,
            }}
          >
            {/* The tier's own first bullet — the same sentence the legend prints
                for this level, from the same table. */}
            {translate(tier.bulletKeys[0])}
          </span>
        </span>
      </button>

      {keySpecs.length > 0 && (
        <CatalogDetailSection title={translate("myMachinery:keySpecs")} defaultOpen>
          {keySpecs.map((spec, index) => (
            // Index key: `extractKeySpecs` preserves v1's behaviour of letting
            // two aliases resolve to the SAME spec, so labels are not guaranteed
            // unique. The list is order-stable and never reordered.
            // eslint-disable-next-line react/no-array-index-key
            <CatalogDetailSpecRow
              key={`${spec.label}-${index}`}
              label={spec.label}
              value={spec.value}
              first={index === 0}
            />
          ))}
        </CatalogDetailSection>
      )}

      {litDocRows.length > 0 && (
        <CatalogDetailSection
          title={translate("myMachinery:v2.catalog.documentation")}
          count={presentation.docCount}
        >
          {litDocRows.map((row, index) => (
            <CatalogDetailSpecRow
              key={row.docType}
              label={translate(V2_DOC_TYPE_KEYS[row.docType])}
              value={String(row.count)}
              first={index === 0}
            />
          ))}
        </CatalogDetailSection>
      )}

      <CatalogDetailSection title={translate("myMachinery:specifications")}>
        <div style={{ padding: "10px 13px", minWidth: 0 }}>
          {/* `touch` IS A LITERAL BECAUSE THIS FILE IS ONE (M2.16e). It is the
              katalóg detail BELOW 1280 and nothing else — CatalogDetailV2
              mounts it only on `!isDesktop`, i.e. exactly when the module's
              `V2_BELOW_DESKTOP` predicate matches — so the ">= 44px both axes"
              law applies to everything in it unconditionally. Without this the
              sheet's metric/imperial segment stayed at the artboard's ~28px
              while the collapse-all beside it in the SAME ROW was raised to 44
              by the module-wide `.btn` floor. `profileHost` is deliberately NOT
              passed: board 23's toolbar reorder is the profile's, and this
              surface keeps artboard 1006's order at every width. */}
          <SpecSheetView
            key={model.id}
            sheet={sheet}
            label={translate("myMachinery:specifications")}
            phone={phone}
            touch
          />
        </div>
      </CatalogDetailSection>
    </div>
  );
};

export default CatalogDetailMobile;
